import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarRange, CalendarX, Check, CornerDownLeft } from 'lucide-react'
import {
  countRangeDays,
  countWorkingDays,
  formatCompactRange,
  getRangePresets,
  isSameCalendarDay,
} from '../utils/dateRange'
import type { DayRange, RangePresetKey } from '../utils/dateRange'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'

interface DateRangePanelProps {
  /** Local (unsaved) start and due; either may be missing while picking. */
  start?: Date
  due?: Date
  month: Date
  onMonthChange: (month: Date) => void
  /** A calendar day was clicked; `extend` when Shift was held. */
  onDayClick: (date: Date, extend: boolean) => void
  onPreset: (range: DayRange) => void
  onClear: () => void
  /** The trimmed search query; while set, the preset list shows the parse result. */
  query: string
  parsedRange: DayRange | null
  onApplyParsed: () => void
  locale: string
  /** Mobile: one column and one month. */
  stacked: boolean
  /** Changes when the picker opens so "this week" never goes stale. */
  refreshKey?: unknown
}

const rowClass =
  'mx-1 grid h-8 w-[calc(100%-8px)] grid-cols-[16px_minmax(0,1fr)_auto_14px] items-center gap-[9px] rounded-[7px] px-2.5 text-left text-[13px]'

const PRESET_LABEL_KEYS: Record<RangePresetKey, string> = {
  this_week: 'picker.date.range.thisWeek',
  next_week: 'picker.date.range.nextWeek',
  next_2_weeks: 'picker.date.range.next2Weeks',
  this_month: 'picker.date.range.thisMonth',
}

/**
 * The Range body of the due-date picker (design 31b, "Due date · range"):
 * range presets with their span on the right, the picked START / DUE with a
 * day count, and two months side by side, since ranges often cross a month end.
 * Today gets a hairline ring; the range is a brand tint between two ink ends.
 */
export function DateRangePanel({
  start,
  due,
  month,
  onMonthChange,
  onDayClick,
  onPreset,
  onClear,
  query,
  parsedRange,
  onApplyParsed,
  locale,
  stacked,
  refreshKey,
}: DateRangePanelProps) {
  const { t } = useTranslation('common')
  const { date: fmtDate } = useDateTimeFormatters()

  // `refreshKey` is a deliberate dep: presets are recomputed on open.
  const presets = useMemo(() => getRangePresets(new Date()), [refreshKey])

  const current: DayRange | null = start && due ? { start, due } : null
  const days = current ? countRangeDays(current) : 0

  const formatDay = (date: Date) =>
    fmtDate(date, { weekday: 'short', month: 'short', day: 'numeric' })

  const presetMeta = (key: RangePresetKey, range: DayRange) => {
    if (key === 'next_2_weeks')
      return t('picker.date.range.daysShort', { count: countRangeDays(range) })
    if (key === 'this_month') return fmtDate(range.start, { month: 'short' })
    return formatCompactRange(range, locale)
  }

  const isActive = (range: DayRange) =>
    !!start &&
    !!due &&
    isSameCalendarDay(range.start, start) &&
    isSameCalendarDay(range.due, due)

  const list = query ? (
    parsedRange ? (
      <button
        type="button"
        onClick={onApplyParsed}
        className={cn(rowClass, 'bg-muted')}
      >
        <CornerDownLeft className="size-3.5 text-muted-foreground" />
        <span className="truncate">
          {formatCompactRange(parsedRange, locale)}
        </span>
        <span className="font-mono text-[10.5px] text-muted-foreground">
          {t('picker.date.range.daysShort', {
            count: countRangeDays(parsedRange),
          })}
        </span>
        <span />
      </button>
    ) : (
      <div className="px-3 py-1.5 text-[12.5px] text-muted-foreground">
        {t('picker.date.range.noMatch')}
      </div>
    )
  ) : (
    <div role="group" aria-label={t('picker.date.range.presets')}>
      {presets.map((preset) => (
        <button
          key={preset.key}
          type="button"
          onClick={() => onPreset(preset.range)}
          className={cn(rowClass, 'hover:bg-muted')}
        >
          <CalendarRange className="size-3.5 text-muted-foreground" />
          <span className="truncate">{t(PRESET_LABEL_KEYS[preset.key])}</span>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            {presetMeta(preset.key, preset.range)}
          </span>
          <span className="grid place-items-center">
            {isActive(preset.range) ? <Check className="size-[13px]" /> : null}
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={onClear}
        className={cn(rowClass, 'text-muted-foreground hover:bg-muted')}
      >
        <CalendarX className="size-3.5" />
        <span className="truncate">{t('picker.date.range.clear')}</span>
        <span />
        <span />
      </button>
    </div>
  )

  const summary = (
    <div className="space-y-1.5 px-3.5 py-2.5" aria-live="polite">
      {(['start', 'due'] as const).map((which) => {
        const value = which === 'start' ? start : due
        return (
          <div key={which} className="flex items-baseline gap-2">
            <span className="w-9 shrink-0 text-[10.5px] font-semibold uppercase tracking-[.08em] text-muted-foreground">
              {t(`picker.date.range.${which}`)}
            </span>
            <span
              className={cn(
                'truncate text-[13px]',
                value ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {value ? formatDay(value) : t('picker.date.range.notSet')}
            </span>
          </div>
        )
      })}
      {current && (
        <p className="pt-0.5 text-[12px] text-muted-foreground">
          {t('picker.date.range.days', { count: days })}
          {' · '}
          {t('picker.date.range.workingDays', {
            count: countWorkingDays(current),
          })}
        </p>
      )}
    </div>
  )

  return (
    <div
      className={cn(
        stacked ? 'flex min-w-0 flex-col overflow-x-hidden' : 'grid grid-cols-[232px_auto]',
      )}
    >
      <div
        className={cn(
          stacked ? 'border-b border-border/70' : 'border-r border-border/70',
        )}
      >
        <div className="py-1">{list}</div>
        <div className="border-t border-border/70" />
        {summary}
      </div>
      <div className="flex w-full max-w-full justify-center overflow-x-hidden">
        <Calendar
          mode="range"
          month={month}
          onMonthChange={onMonthChange}
          numberOfMonths={stacked ? 1 : 2}
          showOutsideDays={false}
          selected={{ from: start, to: due }}
          onSelect={(_range, triggerDate, _modifiers, event) => {
            onDayClick(triggerDate, event.shiftKey)
          }}
          aria-label={t('picker.date.range.calendarLabel')}
          className={cn(
            'bg-transparent p-3 [--cell-size:--spacing(7)]',
            stacked && 'mx-auto w-full max-w-[280px]',
          )}
          classNames={{
            ...(stacked
              ? {
                  root: 'w-full max-w-full',
                  months: 'relative flex w-full max-w-full flex-col gap-4',
                  month: 'flex w-full min-w-0 flex-col gap-4',
                  month_grid: 'w-full table-fixed border-collapse',
                }
              : { months: 'relative flex flex-row gap-4' }),
            today:
              'rounded-md [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-foreground/35',
            range_start: 'rounded-l-md bg-brand-tint',
            range_middle: 'rounded-none bg-brand-tint',
            range_end: 'rounded-r-md bg-brand-tint',
            day_button: cn(
              'text-[12.5px]',
              'data-[range-start=true]:bg-foreground data-[range-start=true]:text-background',
              'data-[range-end=true]:bg-foreground data-[range-end=true]:text-background',
              'data-[range-middle=true]:bg-transparent data-[range-middle=true]:text-brand-tint-foreground',
              'data-[selected-single=true]:bg-foreground data-[selected-single=true]:text-background',
            ),
          }}
        />
      </div>
    </div>
  )
}
